import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { 
  FaLightbulb, 
  FaClipboardList, 
  FaCode, 
  FaCheckCircle,
  FaRocket,
  FaTools
} from "react-icons/fa";

// Process step component
const ProcessStep = ({ icon: Icon, title, description, index, isLast, isRTL }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      viewport={{ once: true }}
      className="relative flex flex-col md:flex-row items-center md:items-start gap-6"
    >
      {/* Icon circle */}
      <div className="flex-shrink-0 relative z-10">
        <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center text-black">
          <Icon size={24} />
        </div>
        
        {/* Vertical line connecting steps */}
        {!isLast && (
          <div className="absolute top-16 left-1/2 w-1 h-24 bg-gray-800 -translate-x-1/2 hidden md:block"></div>
        )}
      </div>
      
      {/* Content */}
      <div className={`flex-grow text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
        <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
        <p className="text-gray-400">{description}</p>
      </div>
    </motion.div>
  );
};

const DevProcessSection = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const location = useLocation();
  
  const isSoftwareDevelopmentPage = location.pathname === "/software-development";
  
  const handleConsultationClick = (e) => {
    if (isSoftwareDevelopmentPage) {
      e.preventDefault();
      document.getElementById('software-contact').scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Process steps data
  const processSteps = [
    {
      icon: FaLightbulb,
      title: t("Discovery & Planning"),
      description: t("We begin by understanding your business needs, target audience, and project goals. Our team conducts thorough research and creates a detailed project roadmap.")
    },
    {
      icon: FaClipboardList,
      title: t("Design & Prototyping"),
      description: t("Our designers create wireframes and interactive prototypes to visualize the solution. We collaborate closely with you to refine the user experience and interface.")
    },
    {
      icon: FaCode,
      title: t("Development"),
      description: t("Our experienced developers build your solution using modern technologies and best practices. We follow agile methodologies with regular sprints and updates.")
    },
    {
      icon: FaCheckCircle,
      title: t("Testing & Quality Assurance"),
      description: t("We conduct rigorous testing to ensure your software is bug-free, secure, and performs optimally across all devices and platforms.")
    },
    {
      icon: FaRocket,
      title: t("Deployment"),
      description: t("Once approved, we deploy your solution to production environments, ensuring a smooth transition and minimal disruption to your operations.")
    },
    {
      icon: FaTools,
      title: t("Maintenance & Support"),
      description: t("Our relationship doesn't end at launch. We provide ongoing maintenance, updates, and support to ensure your software continues to perform at its best.")
    }
  ];
  
  return (
    <div id="dev-process" className="bg-gradient-to-b from-black to-gray-900 py-20">
      <div className="container mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">
            {t("Our Development")} <span className="text-primary">{t("Process")}</span>
          </h2>
          <p className={`text-gray-400 max-w-2xl mx-auto `}>
            {t("We follow a structured and transparent development process to ensure successful project delivery and client satisfaction.")}
          </p>
        </motion.div>
        
        <div className="max-w-4xl mx-auto">
          <div className={`space-y-12 md:space-y-0 md:flex md:flex-col md:gap-16 ${isRTL ? 'rtl' : ''}`}>
            {processSteps.map((step, index) => (
              <ProcessStep
                key={index}
                icon={step.icon}
                title={step.title}
                description={step.description}
                index={index}
                isLast={index === processSteps.length - 1}
                isRTL={isRTL}
              />
            ))}
          </div>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mt-20 bg-gray-900 p-8 rounded-lg border border-gray-800 max-w-3xl mx-auto"
        >
          <h3 className="text-2xl font-bold text-white mb-4 text-center">
            {t("Ready to Start Your Project?")}
          </h3>
          <p className="text-gray-400 text-center mb-6">
            {t("Let's discuss how our development process can be tailored to meet your specific business needs.")}
          </p>
          <div className="flex justify-center">
            <a
              href={isSoftwareDevelopmentPage ? "#software-contact" : "#contact"}
              onClick={handleConsultationClick}
              className="inline-block bg-primary text-black px-8 py-3 rounded-lg font-bold hover:bg-yellow-400 transition-colors"
            >
              {t("schedule_consultation")}
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default DevProcessSection;
